import React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
    Box,
    Drawer,
    List,
    Divider,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip
} from '@mui/material';

// MUI Icons
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import SchoolIcon from '@mui/icons-material/School';
import BusinessIcon from '@mui/icons-material/Business';
import LayersIcon from '@mui/icons-material/Layers';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import MapIcon from '@mui/icons-material/Map';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import LogoutIcon from '@mui/icons-material/Logout';
import { Schedule, Store } from '@mui/icons-material';

const StyledDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open, drawerwidth }) => ({
        width: drawerwidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme, drawerwidth),
            '& .MuiDrawer-paper': openedMixin(theme, drawerwidth),
        }),
        ...(!open && {
            ...closedMixin(theme, drawerwidth),
            '& .MuiDrawer-paper': closedMixin(theme, drawerwidth),
        }),
    }),
);

const openedMixin = (theme, drawerwidth) => ({
    width: drawerwidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    backgroundColor: theme.palette.background.default,
    borderRight: '1px solid rgba(0, 0, 0, 0.08)',
});

const closedMixin = (theme, drawerwidth) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    backgroundColor: theme.palette.background.default,
    borderRight: '1px solid rgba(0, 0, 0, 0.08)',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
}));

const Sidebar = ({ open, onItemClick, drawerWidth, currentScreen }) => {
    const theme = useTheme();

    // Group menu items into categories
    const menuItems = [
        {
            category: 'Attendance Management', items: [
                { id: 'report', text: 'Reports', icon: <AssessmentIcon /> },
                { id: 'recognition', text: 'Attendance', icon: <EventNoteIcon /> },
            ]
        },
        {
            category: 'People Management', items: [
                { id: 'staff', text: 'Employees', icon: <PeopleIcon /> },
                { id: 'schedule', text: 'Schedule', icon: <Schedule /> },
            ]
        },
        {
            category: 'Office Management', items: [
                { id: 'location', text: 'Locations', icon: <GpsFixedIcon /> },
                { id: 'company', text: 'Company', icon: <Store /> },
                { id: 'department', text: 'Departments', icon: <LayersIcon /> },
                { id: 'designation', text: 'Designations', icon: <GroupWorkIcon /> },
                { id: 'device', text: 'Devices', icon: <PhoneAndroidIcon /> },
            ]
        },
    ];

    const renderMenuItems = (items) => {
        return items.map((item) => (
            <ListItem key={item.id} disablePadding sx={{ display: 'block', mb: 0.5 }}>
                <Tooltip title={open ? "" : item.text} placement="right">
                    <ListItemButton
                        onClick={() => onItemClick(item.id)}
                        sx={{
                            minHeight: 48,
                            justifyContent: open ? 'initial' : 'center',
                            px: 2.5,
                            borderRadius: 1,
                            bgcolor: currentScreen === item.id ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                            '&:hover': {
                                bgcolor: currentScreen === item.id ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 0,
                                mr: open ? 3 : 'auto',
                                justifyContent: 'center',
                                color: currentScreen === item.id ? 'primary.main' : 'text.secondary',
                            }}
                        >
                            {item.icon}
                        </ListItemIcon>
                        <ListItemText
                            primary={item.text}
                            sx={{
                                opacity: open ? 1 : 0,
                                '& .MuiTypography-root': {
                                    fontWeight: currentScreen === item.id ? 600 : 400,
                                    color: currentScreen === item.id ? 'primary.main' : 'text.primary',
                                }
                            }}
                        />
                    </ListItemButton>
                </Tooltip>
            </ListItem>
        ));
    };

    return (
        <StyledDrawer
            variant="permanent"
            open={open}
            drawerwidth={drawerWidth}
        >
            <DrawerHeader />

            <Box sx={{ px: open ? 2 : 0, py: 1 }}>
                {menuItems.map((group, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                        {open && (
                            <Box
                                sx={{
                                    px: 3,
                                    py: 1,
                                    typography: 'caption',
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                    fontSize: '0.7rem',
                                }}
                            >
                                {group.category}
                            </Box>
                        )}
                        <List disablePadding>
                            {renderMenuItems(group.items)}
                        </List>
                        {index < menuItems.length - 1 && <Divider sx={{ my: 1, mx: open ? 2 : 1 }} />}
                    </Box>
                ))}
            </Box>
        </StyledDrawer>
    );
};

export default Sidebar;